import {ACCESS_TOKEN} from '@constants/auth.constant'
import {LocalStorageService} from '@services/utils/localStorage.service'
import {axiosIns} from 'config/api.config'
import {ENV} from 'config/ENV.config'

/**
 * chat-service REST. Every write goes here (the socket only pushes). Each call resolves to the
 * AppResponse envelope; read `.body`.
 */
export const ChatService = {
  ticket: (): Promise<any> => axiosIns.post('/ws-ticket'),

  conversations: (): Promise<any> => axiosIns.get('/conversation/list'),
  conversation: (id: string): Promise<any> => axiosIns.get(`/conversation/${id}`),
  direct: (userId: string): Promise<any> => axiosIns.post('/conversation/direct', {userId}),
  group: (payload: {
    title: string
    description?: string
    memberIds: string[]
    historyPolicy?: string
  }): Promise<any> => axiosIns.post('/conversation/group', payload),
  update: (id: string, payload: Record<string, any>): Promise<any> =>
    axiosIns.put(`/conversation/${id}`, payload),
  addMembers: (id: string, userIds: string[]): Promise<any> =>
    axiosIns.post(`/conversation/${id}/members`, {userIds}),
  removeMember: (id: string, userId: string): Promise<any> =>
    axiosIns.delete(`/conversation/${id}/members/${userId}`),
  setRole: (id: string, userId: string, role: 'ADMIN' | 'MEMBER'): Promise<any> =>
    axiosIns.put(`/conversation/${id}/members/${userId}/role`, {role}),
  leave: (id: string): Promise<any> => axiosIns.post(`/conversation/${id}/leave`),
  read: (id: string, seq: number): Promise<any> => axiosIns.post(`/conversation/${id}/read`, {seq}),
  state: (
    id: string,
    payload: {mute?: string; pinned?: boolean; archived?: boolean; markedUnread?: boolean}
  ): Promise<any> => axiosIns.put(`/conversation/${id}/state`, payload),

  history: (
    id: string,
    params: {beforeSeq?: number; afterSeq?: number; aroundSeq?: number; limit?: number}
  ): Promise<any> => axiosIns.get(`/conversation/${id}/messages`, {params}),
  send: (
    id: string,
    payload: {
      clientMsgId: string
      body: string
      replyToId?: string | null
      attachmentIds?: string[]
      mentions?: string[]
    }
  ): Promise<any> => axiosIns.post(`/conversation/${id}/messages`, payload),
  /** One file per request; the console fans out. */
  upload: (
    conversationId: string,
    file: File,
    onProgress?: (fraction: number) => void
  ): Promise<any> => {
    const form = new FormData()
    form.append('conversationId', conversationId)
    form.append('file', file)
    return axiosIns.post('/attachment', form, {
      onUploadProgress: (e: any) => onProgress && e.total && onProgress(e.loaded / e.total),
    })
  },
  react: (messageId: string, emoji: string | null): Promise<any> =>
    axiosIns.put(`/message/${messageId}/reaction`, {emoji}),
  forward: (messageId: string, conversationIds: string[]): Promise<any> =>
    axiosIns.post(`/message/${messageId}/forward`, {conversationIds}),
  info: (messageId: string): Promise<any> => axiosIns.get(`/message/${messageId}/info`),
  media: (id: string, kind: 'media' | 'docs' | 'links', beforeSeq?: number): Promise<any> =>
    axiosIns.get(`/conversation/${id}/media`, {params: {kind, beforeSeq, limit: 60}}),
  edit: (messageId: string, body: string): Promise<any> =>
    axiosIns.put(`/message/${messageId}`, {body}),
  remove: (messageId: string): Promise<any> => axiosIns.delete(`/message/${messageId}`),

  members: (id: string, params: {q?: string; offset?: number; limit?: number}): Promise<any> =>
    axiosIns.get(`/conversation/${id}/members`, {params}),

  directory: (q: string, limit = 50, offset = 0): Promise<any> =>
    axiosIns.get('/directory', {params: {q, limit, offset}}),
  unread: (): Promise<any> => axiosIns.get('/unread/summary'),
  /** Messages I can read: across my chats, or in one ({@code conversationId}). */
  search: (params: {
    q: string
    conversationId?: string
    senderId?: string
    from?: string
    to?: string
    limit?: number
    offset?: number
  }): Promise<any> => axiosIns.get('/search', {params}),
  settings: (): Promise<any> => axiosIns.get('/settings'),
  presence: (userIds: string[]): Promise<any> =>
    axiosIns.get('/presence', {params: {userIds: userIds.join(',')}}),
}

/** CHAT_ADMIN: settings, the HR channel sync, and every group and channel (never a DM). */
export const ChatAdminService = {
  settings: (): Promise<any> => axiosIns.get('/admin/settings'),
  saveSettings: (payload: Record<string, any>): Promise<any> =>
    axiosIns.put('/admin/settings', payload),
  syncChannels: (): Promise<any> => axiosIns.post('/admin/channel-sync'),
  list: (payload: any): Promise<any> => axiosIns.post('/admin/conversation/list', payload),
  detail: (id: string): Promise<any> => axiosIns.get(`/admin/conversation/${id}`),
  members: (id: string, params: {q?: string; offset?: number; limit?: number}): Promise<any> =>
    axiosIns.get(`/admin/conversation/${id}/members`, {params}),
  update: (id: string, payload: Record<string, any>): Promise<any> =>
    axiosIns.put(`/admin/conversation/${id}`, payload),
  addMembers: (id: string, userIds: string[]): Promise<any> =>
    axiosIns.post(`/admin/conversation/${id}/members`, {userIds}),
  removeMember: (id: string, userId: string): Promise<any> =>
    axiosIns.delete(`/admin/conversation/${id}/members/${userId}`),
  setRole: (id: string, userId: string, role: 'ADMIN' | 'MEMBER'): Promise<any> =>
    axiosIns.put(`/admin/conversation/${id}/members/${userId}/role`, {role}),
}

const MIME = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

/**
 * Save a binary endpoint's file under the name the server gives it. Fetched with the bearer token
 * (an anchor cannot carry it); a refusal (a range over the cap, an expired session: core answers
 * 200 + JSON) is detected by its content type and thrown as its message, never saved as a file.
 */
const downloadBinary = async (path: string, format: 'pdf' | 'xlsx', fallbackName: string) => {
  const res = await fetch(`${ENV.apiEndpoint}${path}`, {
    headers: {
      Authorization: `Bearer ${LocalStorageService.get(ACCESS_TOKEN)}`,
      Accept: `${MIME[format]}, application/json`,
    },
  })
  const type = res.headers.get('content-type') || ''
  if (!res.ok || type.includes('application/json')) {
    let msg = 'The export could not be produced.'
    try {
      msg = (await res.json())?.msg || msg
    } catch {
      // not JSON either
    }
    throw new Error(msg)
  }
  const disposition = res.headers.get('content-disposition') || ''
  const name =
    decodeURIComponent(/filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1] || '') ||
    /filename="?([^";]+)"?/i.exec(disposition)?.[1] ||
    `${fallbackName}.${format}`
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/**
 * The company admin's read-only conversation review: COMPANY_ADMIN with an explicit CHAT_COMPLIANCE
 * grant. Every call is recorded in chat.access_log by the server; none marks anything read.
 */
export const ChatComplianceService = {
  /** 200 when this person may open the review; the usual 403 otherwise. */
  access: (): Promise<any> => axiosIns.get('/compliance/access'),
  list: (payload: any): Promise<any> => axiosIns.post('/compliance/conversation/list', payload),
  detail: (id: string): Promise<any> => axiosIns.get(`/compliance/conversation/${id}`),
  /** Older than beforeSeq, newer than afterSeq, around aroundSeq (a search hit), or the newest. */
  messages: (
    id: string,
    params: {beforeSeq?: number; afterSeq?: number; aroundSeq?: number}
  ): Promise<any> =>
    axiosIns.get(`/compliance/conversation/${id}/messages`, {params: {...params, limit: 100}}),
  /** Download the transcript ({@code pdf} or {@code xlsx}) under the server's file name. */
  exportTranscript: (id: string, format: 'pdf' | 'xlsx') =>
    downloadBinary(
      `/compliance/conversation/${id}/export?format=${format}`,
      format,
      'chat-transcript'
    ),
  /** The access log with the screen's filters, as a spreadsheet. */
  exportAccessLog: (filters: Record<string, string>) =>
    downloadBinary(
      `/compliance/access-log/export?${new URLSearchParams(
        Object.entries(filters).filter(([, v]) => !!v)
      ).toString()}`,
      'xlsx',
      'chat-access-log'
    ),
  /** The access log, newest first, filtered and paged like a grid. */
  accessLog: (payload: any): Promise<any> => axiosIns.post('/compliance/access-log/list', payload),
  /** Place (with a reason) or release a legal hold: retention never purges a held conversation. */
  legalHold: (id: string, hold: boolean, reason?: string): Promise<any> =>
    axiosIns.put(`/compliance/conversation/${id}/legal-hold`, {hold, reason}),
  /** Messages of every conversation (deleted ones and file names too), paged like a grid. */
  search: (payload: any): Promise<any> => axiosIns.post('/compliance/search', payload),
}
